import React, { ReactEventHandler, useCallback } from 'react';
import {
    Flex,
    SideNavItem,
    MenuList,
    MenuListGroup,
    MenuListSeparator,
    InlineBanner,
    Text,
} from '@cad-arc';
import { DexFormView, DexDispatcherFormEnumType } from '@mark43/cad-api';
import { compact } from 'lodash';
import { LoadingIndicator } from '../../common/components/loading-indicator';
import { observer } from 'mobx-react-lite';
import { Panel } from '../../panels/panel';
import { PanelTitle } from '../../panels/panel-title';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { intersperse } from '../../common/helpers/intersperse';
import { useOpenWorkspacePanel } from '../../panels/use-open-workspace-panel';
import { useDexFormsQuery } from './use-dex-forms-query';
import { useDexFormView } from './use-dex-form-view';
import { DexFormEntitiesKeys, DEX_FORMS_ORDER, DexFormTitlesMap } from './constants';
import { SelectedOriAliasProvider } from './use-selected-ori-alias';
import { getUniversalSearchFormLabel } from '../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { getDataExchangeFormPanelMatcher } from '../../common/helpers/get-data-exchange-form-panel-matcher';

export function DexFormsPanelBody({
    selectedDispatcherFormType,
    getOpenDexFormPanel,
}: {
    getOpenDexFormPanel: (form: DexFormView) => ReactEventHandler<HTMLButtonElement>;
    selectedDispatcherFormType?: DexDispatcherFormEnumType;
}): React.ReactElement {
    const { data, isInitialLoading, isError, refetch } = useDexFormsQuery();
    const dexFormsResponse = useDexFormView({ dexFormsResponse: data });

    if (isInitialLoading) {
        return <LoadingIndicator />;
    }
    if (isError) {
        return (
            <Flex h="100%" w="100%" justifyContent="center" alignItems="center" padding="16px">
                <InlineBanner
                    status="error"
                    title="An error occurred while loading DEx Forms"
                    actions={[{ children: 'Retry', onClick: () => refetch() }]}
                />
            </Flex>
        );
    }
    if (!dexFormsResponse) {
        return (
            <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                <Text variant="headingXs">No DEx forms found</Text>
            </Flex>
        );
    }

    const vehicleMasterQueryForm = dexFormsResponse.vehicleFormBootstrapView?.dexFormView;
    const vehicleByNameForm = dexFormsResponse.vehicleByNameFormBootstrapView?.dexFormView;
    const vehicleRegistrationByPlateForm =
        dexFormsResponse.vehicleRegistrationByPlateFormBootstrapView?.dexFormView;
    const vehicleRegistrationByVinForm =
        dexFormsResponse.vehicleRegistrationByVinFormBootstrapView?.dexFormView;
    const vehicleRegistrationByNameForm =
        dexFormsResponse.vehicleRegistrationByNameFormBootstrapView?.dexFormView;
    const vehicleRegistrationByOtherForm =
        dexFormsResponse.vehicleRegistrationByOtherFormBootstrapView?.dexFormView;
    const vehicleRegstrationByRecentQueryFilesForm =
        dexFormsResponse.vehicleRegistrationByRecentQueryFilesFormBoostrapView?.dexFormView;
    const lojackMasterQueryForm = dexFormsResponse.lojackFormBootstrapView?.dexFormView;
    const vehicleStolenForm = dexFormsResponse.vehicleStolenFormBootstrapView?.dexFormView;
    const vehicleForms = compact([
        vehicleMasterQueryForm,
        vehicleByNameForm,
        vehicleRegistrationByPlateForm,
        vehicleRegistrationByVinForm,
        vehicleRegistrationByNameForm,
        vehicleRegistrationByOtherForm,
        vehicleRegstrationByRecentQueryFilesForm,
        lojackMasterQueryForm,
        vehicleStolenForm,
    ]);

    const personMasterQueryForm = dexFormsResponse.personFormBootstrapView?.dexFormView;
    const personProbationForm = dexFormsResponse.personProbationFormBootstrapView?.dexFormView;
    const personSexOffenderForm = dexFormsResponse.personSexOffenderFormBootstrapView?.dexFormView;
    const personWarrantDetailForm =
        dexFormsResponse.personWarrantDetailFormBootstrapView?.dexFormView;
    const criminalHistoryIndexForm =
        dexFormsResponse.criminalHistoryIndexFormBootstrapView?.dexFormView;
    const criminalHistoryRecordRequestForm =
        dexFormsResponse.criminalHistoryRecordRequestFormBootstrapView?.dexFormView;
    const criminalHistoryIdentityQueryForm =
        dexFormsResponse.criminalHistoryIdentityQueryFormBootstrapView?.dexFormView;
    const criminalHistoryFullRecordForm =
        dexFormsResponse.criminalHistoryFullRecordFormBootstrapView?.dexFormView;
    const crimsForm = dexFormsResponse.crimsFormBootstrapView?.dexFormView;
    const imageQueryForm = dexFormsResponse.imageFormBootstrapView?.dexFormView;
    const personConcealedWeaponsForm =
        dexFormsResponse.firearmConcealedWeaponsFormBootstrapView?.dexFormView;
    const personForms = compact([
        personMasterQueryForm,
        crimsForm,
        personProbationForm,
        personSexOffenderForm,
        personWarrantDetailForm,
        personConcealedWeaponsForm,
    ]);
    const imageQueryForms = compact([imageQueryForm]);
    const criminalHistoryForms = compact([
        criminalHistoryIndexForm,
        criminalHistoryRecordRequestForm,
        criminalHistoryIdentityQueryForm,
        criminalHistoryFullRecordForm,
    ]);

    const firearmMasterQueryForm = dexFormsResponse.firearmFormBootstrapView?.dexFormView;

    const firearmOwnersIdForm = dexFormsResponse.firearmsOwnersIdFormBootstrapView?.dexFormView;
    const firearmForms = compact([firearmMasterQueryForm, firearmOwnersIdForm]);

    const articleMasterQueryForm = dexFormsResponse.articleFormBootstrapView?.dexFormView;
    const articleForms = compact([articleMasterQueryForm]);

    const boatMasterQueryForm = dexFormsResponse.boatFormBootstrapView?.dexFormView;
    const boatForms = compact([boatMasterQueryForm]);

    const canadianWantedPersonQueryForm =
        dexFormsResponse.canadianWantedPersonFormBootstrapView?.dexFormView;
    const canadianDriverLicenseQueryForm =
        dexFormsResponse.canadianDriverLicenseFormBootstrapView?.dexFormView;
    const canadianVehicleRegistrationQueryForm =
        dexFormsResponse.canadianVehicleRegistrationFormBootstrapView?.dexFormView;

    const canadianQueryForms = compact([
        canadianWantedPersonQueryForm,
        canadianDriverLicenseQueryForm,
        canadianVehicleRegistrationQueryForm,
    ]);

    const FormsMap = {
        [DexFormEntitiesKeys.Vehicles]: vehicleForms,
        [DexFormEntitiesKeys.Persons]: personForms,
        [DexFormEntitiesKeys.Image]: imageQueryForms,
        [DexFormEntitiesKeys.CriminalHistory]: criminalHistoryForms,
        [DexFormEntitiesKeys.Firearms]: firearmForms,
        [DexFormEntitiesKeys.Articles]: articleForms,
        [DexFormEntitiesKeys.Boats]: boatForms,
        [DexFormEntitiesKeys.CanadianQuery]: canadianQueryForms,
    };

    return (
        //TODO: FR-next replace MenuListGroup with a group for nav items after RND-16670 will be done
        <MenuList style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            {intersperse(
                compact(
                    DEX_FORMS_ORDER.map((formKey) => {
                        const forms = FormsMap[formKey];
                        return (
                            forms?.length > 0 && (
                                <MenuListGroup title={DexFormTitlesMap[formKey]} key={formKey}>
                                    {forms.map((form) => (
                                        <SideNavItem
                                            as="button"
                                            key={form.dexQuery}
                                            label={form.title}
                                            isActive={
                                                selectedDispatcherFormType ===
                                                form.dexDispatcherForm
                                            }
                                            onClick={getOpenDexFormPanel(form)}
                                        />
                                    ))}
                                </MenuListGroup>
                            )
                        );
                    })
                ),
                <MenuListSeparator />
            )}
        </MenuList>
    );
}

export const DexFormsPanel = observer(function DexFormsPanel({
    panel,
}: {
    panel: WorkspacePanel;
}): React.ReactElement {
    const openWorkspacePanel = useOpenWorkspacePanel();
    const { dexNextEnabled } = useEnabledFeatures();

    const getOpenDexFormPanel = useCallback(
        (form: DexFormView): ReactEventHandler<HTMLButtonElement> => () =>
            // need to open the form panel in the next cycle so the click in this panel doesn't steal back focus
            setTimeout(() => {
                openWorkspacePanel({
                    matchExistingPanel: getDataExchangeFormPanelMatcher({
                        dexForm: form.dexDispatcherForm,
                        dexNextEnabled,
                    }),
                    workspacePanelConfig: {
                        workspacePanelType: 'DATA_EXCHANGE_FORM',
                        dexForm: form.dexDispatcherForm,
                    },
                });
            }, 0),
        [openWorkspacePanel, dexNextEnabled]
    );

    return (
        <Panel
            panel={panel}
            header={<PanelTitle>{`${getUniversalSearchFormLabel(dexNextEnabled)}`}</PanelTitle>}
            body={
                <SelectedOriAliasProvider>
                    <DexFormsPanelBody getOpenDexFormPanel={getOpenDexFormPanel} />
                </SelectedOriAliasProvider>
            }
        />
    );
});
